import { useState, useRef, useEffect } from 'react';
import { BellIcon, Bars3Icon } from '@heroicons/react/24/outline';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { formatRelative } from '../../utils/formatters';
import BrandLogo from '../brand/BrandLogo';

export default function Header({ title, navOpen = false, onOpenNav }) {
  const [showNotif, setShowNotif] = useState(false);
  const dropRef = useRef(null);
  const bellRef = useRef(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: notifData } = useQuery('notifications', () =>
    api.get('/dashboard/notifications').then(r => r.data),
    { refetchInterval: 30000 }
  );

  const markRead = useMutation(
    id => api.patch(`/dashboard/notifications/${id}/read`),
    { onSuccess: () => queryClient.invalidateQueries('notifications') }
  );

  const markAllRead = useMutation(
    () => api.patch('/dashboard/notifications/read-all'),
    { onSuccess: () => queryClient.invalidateQueries('notifications') }
  );

  useEffect(() => {
    const handler = e => {
      if (dropRef.current && !dropRef.current.contains(e.target)) setShowNotif(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Escape closes the panel and hands focus back to the bell.
  useEffect(() => {
    if (!showNotif) return undefined;
    const handler = e => {
      if (e.key !== 'Escape') return;
      setShowNotif(false);
      bellRef.current?.focus();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [showNotif]);

  const notifications = notifData?.notifications || [];
  const unread = notifData?.unread || 0;

  return (
    <header className="sticky top-0 z-sticky bg-white/95 backdrop-blur border-b border-slate-200 px-4 sm:px-6 py-4">
      <div className="flex items-center justify-between gap-3 sm:gap-4">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <button
            type="button"
            onClick={onOpenNav}
            aria-label="Open navigation menu"
            aria-expanded={navOpen}
            aria-controls="admin-sidebar"
            className="lg:hidden p-2.5 -ml-1 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-xl focus-ring transition-colors duration-150"
          >
            <Bars3Icon className="h-5 w-5" aria-hidden="true" />
          </button>
          {/* Dark tile: the mark's artwork is white, so it needs one to be legible. */}
          <div className="hidden sm:flex h-9 w-9 flex-shrink-0 rounded-xl bg-slate-900 items-center justify-center shadow-sm">
            <BrandLogo showWordmark={false} size={24} alt="Genesis mark" />
          </div>
          <div className="min-w-0">
            {/* Deliberately not a heading: every page renders its own <h1> below this. */}
            <p className="text-lg font-semibold text-slate-900 leading-tight truncate">{title}</p>
            <p className="text-xs text-slate-500 truncate">Genesis Creations Talent Hub</p>
          </div>
        </div>

        <div className="relative flex-shrink-0" ref={dropRef}>
          <button
            ref={bellRef}
            type="button"
            onClick={() => setShowNotif(!showNotif)}
            aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
            aria-expanded={showNotif}
            aria-haspopup="true"
            aria-controls="notifications-panel"
            className="relative p-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl focus-ring transition-colors duration-150"
          >
            <BellIcon className="h-5 w-5" aria-hidden="true" />
            {unread > 0 && (
              <span
                aria-hidden="true"
                className="absolute top-1 right-1 h-4 w-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center"
              >
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </button>

          {showNotif && (
            <div
              id="notifications-panel"
              className="absolute right-0 top-full z-dropdown mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden"
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <h2 className="text-sm font-semibold">Notifications</h2>
                {unread > 0 && (
                  <button
                    type="button"
                    onClick={() => markAllRead.mutate()}
                    className="text-xs text-brand-600 hover:underline focus-ring rounded"
                  >
                    Mark all read
                  </button>
                )}
              </div>
              <div className="max-h-80 overflow-y-auto">
                {notifications.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-gray-400 text-center">No notifications</p>
                ) : (
                  notifications.map(n => (
                    <button
                      key={n.id}
                      type="button"
                      onClick={() => {
                        if (!n.is_read) markRead.mutate(n.id);
                        if (n.link) navigate(n.link);
                        setShowNotif(false);
                        bellRef.current?.focus();
                      }}
                      className={`w-full text-left px-4 py-3 hover:bg-gray-50 focus-ring transition-colors duration-150 border-b border-gray-50 ${
                        !n.is_read ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <p className="text-sm font-medium text-gray-900">
                        {n.title}
                        {!n.is_read && <span className="sr-only"> (unread)</span>}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">{n.body}</p>
                      <p className="text-xs text-gray-400 mt-1">{formatRelative(n.created_at)}</p>
                    </button>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
